import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import type { RawParams } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { noop } from "lodash";
import { useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { Stack } from "@similarweb/ui-components/foundations";

import type { getKeywordsDrilldownTableColumns } from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import {
    DEFAULT_SORT,
    getTableColumns as getDefaultTableColumns,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import OrganicLandingPagesEnrichedV2 from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesEnrichedV2";
import {
    OrganicLandingPagesTableTopBarV2,
    availableSerpFeatures,
    availableSerpPaidFeatures,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesTableTopBarV2";
import {
    OrganicLandingPagesV2Wrapper,
    TableWrapper,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/StyledComponents";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { hasQueryParameters } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter } from "app/filters/i18nFilter";
import { TablePageFilterWithQueryParamsEmptyState } from "app/pages/website-analysis/components/TablePageFilterWithQueryParamsEmptyState";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

import {
    getShareValue,
    getUrlSpend,
    getVolumeValue,
} from "../../../../website-analysis/traffic-sources/search/common/utils";
import type { IRule } from "../../../../website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";

import { useLandingPagesEndpoints } from "./landingPagesEndpoints";
import type { IData, IDataRecord } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { ELandingPagesTabs, sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { allTrackers } from "app/services/track/track";

const i18n = i18nFilter();
const pageSize = 100;

const getSelectedPagesFilter = (selectedDomain: string, pageArr: IChosenItemParams, domains: any[]) => {
    if (!selectedDomain?.length) {
        const result = pageArr.find((item) => item.url.startsWith(domains[0].name));
        return JSON.stringify([result]);
    }
    const results = pageArr.filter((item) => selectedDomain.split(",").some((domain) => item.url.startsWith(domain)));
    return JSON.stringify(results);
};

type GetColumnsFunction = (params: RawParams, setTabIndex: (index: number) => void) => any[];

interface IOrganicLandingPagesV2Props {
    keywordSerpTargetState?: string;
    keywordTargetState?: string;
    searchAdsTargetState?: string;
    withAddToDashboard?: boolean;
    getColumnsFunction?: GetColumnsFunction;
    getKeywordExpansionColumnsFunction?: typeof getKeywordsDrilldownTableColumns;
    onSeeAllClick?: (
        trackingEventSuffix: string,
        state: string,
        additionalParams?: Record<string, unknown>,
    ) => (url: string) => void;
}

export const OrganicLandingPagesV2: FunctionComponent<IOrganicLandingPagesV2Props> = ({
    keywordSerpTargetState = "organicsearch_keywordAnalysis_2",
    keywordTargetState = "organicsearch_websites_v2_traffic_keywords",
    searchAdsTargetState = "paidsearch_website_ads",
    withAddToDashboard = true,
    getColumnsFunction = getDefaultTableColumns,
    getKeywordExpansionColumnsFunction,
    onSeeAllClick,
}) => {
    const endpoints = useLandingPagesEndpoints();
    const { params } = useCurrentStateAndParams();
    const chosenItems = useSelector((state: RootState) => {
        const {
            routing: { chosenItems },
        } = state;
        return chosenItems;
    });
    const {
        key,
        pageFilter,
        country,
        selectedDomain,
        webSource,
        isWWW,
        sort = DEFAULT_SORT,
        asc = false,
        Change,
        multiIncludeExcludeKeywords,
        multiIncludeExcludeWebsites,
        selectedPageTab,
        serp,
    } = params;

    const pageArr: IChosenItemParams = pageFilter.length ? JSON.parse(pageFilter) : [];
    const isPageWithQueryParams = hasQueryParameters(pageArr[0].url);

    const excelAllowed = swSettings.current.resources.IsExcelAllowed;
    const domains = chosenSites.sitelistForLegend();
    const [pageNumber, setPageNumber] = useState(0);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    const selectedPagesFilter = getSelectedPagesFilter(selectedDomain, pageArr, domains);

    const { to, from, isWindow } = proNavigator.getApiParams();
    const serpFeatures =
        selectedPageTab === ELandingPagesTabs[ELandingPagesTabs.Organic]
            ? availableSerpFeatures
            : availableSerpPaidFeatures;
    const serpList = serp?.split(",")?.filter((id) => serpFeatures[id]);

    const apiParams = {
        country,
        to,
        from,
        isWindow,
        webSource,
        key: selectedDomain || domains[0].name,
        pageFilterJson: selectedPagesFilter,
        serp: serpList?.length ? serpList : undefined,
        sort,
        asc,
        Change,
        sourceType: sourceTypeMap[params.selectedPageTab],
    };

    const sitesForSerpColumn = domains.filter((site) => site.name === apiParams.key);

    useEffect(() => {
        if (
            selectedDomain &&
            !domains.some((d) => d.name === selectedDomain) &&
            !pageArr.some((d) => d.url === selectedDomain)
        ) {
            proNavigator.updateParams({ selectedDomain: domains[0].name });
        }
    }, [key]);

    const changePageCallback = (page) => {
        allTrackers.dispatchTrackEvent("Pagination", "switch", `page ${page || ""}`);
        setPageNumber(page);
    };

    const transformData = (data: IData) => {
        return {
            ...data,
            Data: (data?.Data ?? []).map((record: IDataRecord) => {
                const shareOfDesktop = record.DesktopClicks / record.Clicks;
                const { TopKeyword, ...recordRest } = record;
                return {
                    ...recordRest,
                    ClicksShare: getShareValue(recordRest.ClicksShare),
                    Clicks: getVolumeValue(recordRest.Clicks),
                    Volume: getVolumeValue(recordRest.Volume),
                    AverageVolume: getVolumeValue(recordRest.AverageVolume),
                    WebSourceShare: shareOfDesktop,
                    SearchTerm: TopKeyword,
                    SiteSerpFeatures: { [apiParams.key]: record.SerpFeatures },
                    SerpScrapeDate: record.LatestKeywordSerpDate,
                    Trend: Object.entries(record?.Trend || {})
                        .map(([date, clicks]) => {
                            return { Key: date, Value: clicks };
                        })
                        .sort((a, b) => {
                            const aDate = dayjs(a.Key);
                            const bDate = dayjs(b.Key);
                            return aDate.isAfter(bDate) ? 1 : -1;
                        }),
                    UrlSpend: getUrlSpend(recordRest.UrlSpend),
                    AdsCount: recordRest.AdsCount,
                };
            }),
        };
    };

    const requestBody = useMemo(() => {
        try {
            const websites: IRule[] = multiIncludeExcludeWebsites ? JSON.parse(multiIncludeExcludeWebsites) : [];
            const keywords: IRule[] = multiIncludeExcludeKeywords ? JSON.parse(multiIncludeExcludeKeywords) : [];
            return [
                ...websites.map((rule) => ({ ...rule, field: "website" })),
                ...keywords.map((rule) => ({ ...rule, field: "keyword" })),
            ];
        } catch (e) {
            Sentry.error("Error making the post request:", e);
        }
    }, [multiIncludeExcludeWebsites, multiIncludeExcludeKeywords]);

    return (
        <Stack gap="24px">
            <OrganicLandingPagesV2Wrapper>
                <TableWrapper>
                    <SWReactTableWrapper
                        initialFilters={{
                            ...apiParams,
                            includeSubDomains: isWWW === "*",
                        }}
                        transformData={transformData}
                        tableOptions={{
                            longLoader: {
                                title: i18n("organic.landing.pages.table.loader.title"),
                                subtitle: i18n("organic.landing.pages.table.loader.subtitle"),
                            },
                            noDataComponent: isPageWithQueryParams ? (
                                <TablePageFilterWithQueryParamsEmptyState />
                            ) : undefined,
                            sitesForSerpColumn,
                            enrichedRowComponentProps: {
                                selectedDomain: apiParams.key,
                                pageSize: pageSize,
                                pageNumber: pageNumber,
                                selectedTabIndex: selectedTabIndex,
                                setSelectedTabIndex: setSelectedTabIndex,
                                excelAllowed: excelAllowed,
                                keywordTargetState,
                                keywordSerpTargetState,
                                searchAdsTargetState,
                                getKeywordExpansionColumnsFunction,
                                onSeeAllClick,
                            },
                            EnrichedRowComponent: OrganicLandingPagesEnrichedV2,
                            enrichedRowComponentAppendTo: ".organic-landing-pages-table",
                            enrichedRowComponentHeight: 640,
                            onEnrichedRowClick: noop,
                            shouldApplyEnrichedRowHeightToCell: false,
                            shouldEnrichRow: (props, index, e) => {
                                const openEnrich = e?.currentTarget?.classList?.contains("enrich");
                                if (openEnrich) {
                                    allTrackers.dispatchTrackEvent("Button", "open", "Organic Landing Pages/Expand");
                                }
                                return openEnrich;
                            },
                            customTableClass: "organic-landing-pages-table",
                        }}
                        serverApi={endpoints.table}
                        tableColumns={getColumnsFunction(params, setSelectedTabIndex)}
                        recordsField="Data"
                        totalRecordsField="TotalCount"
                        changePageCallback={changePageCallback}
                        rowsPerPage={pageSize}
                        pageIndent={1}
                        requestBody={requestBody}
                    >
                        {(topComponentProps) => (
                            <OrganicLandingPagesTableTopBarV2
                                {...topComponentProps}
                                selectedDomain={selectedDomain}
                                domains={domains}
                                chosenItems={chosenItems}
                                excelDownloadUrl={endpoints.excel}
                                excelAllowed={excelAllowed}
                                excelRequestBody={requestBody}
                                withAddToDashboard={withAddToDashboard}
                            />
                        )}
                    </SWReactTableWrapper>
                </TableWrapper>
            </OrganicLandingPagesV2Wrapper>
        </Stack>
    );
};
